// src/pages/Vehicles.js
import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import DownloadIcon from '@mui/icons-material/Download'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { supabase } from '../supabaseClient'

// ✅ Direct Node backend base URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://157.180.43.56:5050'

// ---------- Small helpers for API ----------
async function apiFetchVehicles() {
  const res = await fetch(`${API_BASE_URL}/api/vehicles`)
  if (!res.ok) {
    throw new Error(`Failed to fetch vehicles: ${res.status}`)
  }
  const data = await res.json()
  return data.vehicles || []
}

async function apiCreateVehicle(payload) {
  const res = await fetch(`${API_BASE_URL}/api/vehicles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const errText = await res.text().catch(() => '')
    throw new Error(
      `Failed to create vehicle (status ${res.status}): ${errText || res.statusText}`
    )
  }
  return res.json()
}

async function apiUpdateVehicle(id, payload) {
  const res = await fetch(`${API_BASE_URL}/api/vehicles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const errText = await res.text().catch(() => '')
    throw new Error(
      `Failed to update vehicle (status ${res.status}): ${errText || res.statusText}`
    )
  }
  return res.json()
}

async function apiDeleteVehicle(id) {
  const res = await fetch(`${API_BASE_URL}/api/vehicles/${id}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    const errText = await res.text().catch(() => '')
    throw new Error(
      `Failed to delete vehicle (status ${res.status}): ${errText || res.statusText}`
    )
  }
  return res.json()
}

export default function VehicleList() {
  const [vehicles, setVehicles] = useState([])
  const [currentUserId, setCurrentUserId] = useState(null) // UUID from Supabase auth
  const [isAdmin, setIsAdmin] = useState(false)

  const [formData, setFormData] = useState({
    plate_number: '',
    vehicle_type: '',
    daily_rate: '',
    purchase_price: '',
    contract_start: '',
    contract_end: '',
    driver_name: '',
    license_number: '',
  })
  const [showForm, setShowForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterDriver, setFilterDriver] = useState('')
  const [filterDate, setFilterDate] = useState('')
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editData, setEditData] = useState(null)
  const [page, setPage] = useState(0)

  // =========================
  // Load user + vehicles from API
  // =========================
  const loadUserAndVehicles = useCallback(async () => {
    try {
      // 1) Get authenticated user from Supabase Auth (for now)
      const { data: userData, error: userErr } = await supabase.auth.getUser()
      if (userErr || !userData?.user?.id) {
        console.error('[Vehicles] getUser error or no user:', userErr)
        setCurrentUserId(null)
        setVehicles([])
        setIsAdmin(false)
        return
      }

      const uid = userData.user.id
      setCurrentUserId(uid)

      // 2) Get user role (still from Supabase for now)
      let admin = false
      try {
        const { data: approved, error: roleErr } = await supabase
          .from('approved_users')
          .select('role')
          .eq('user_id', uid)
          .single()

        if (roleErr) {
          console.error('[Vehicles] role fetch error:', roleErr)
        }
        admin = approved?.role === 'admin'
        setIsAdmin(admin)
      } catch (err) {
        console.error('[Vehicles] role check exception:', err)
      }

      // 3) Fetch vehicles from Hetzner Node API
      const allVehicles = await apiFetchVehicles()

      // Admin sees all, normal user only their own (by added_by)
      const list = admin ? allVehicles : allVehicles.filter((v) => v.added_by === uid)
      setVehicles(list)
    } catch (err) {
      console.error('[Vehicles] loadUserAndVehicles error:', err)
      setVehicles([])
    }
  }, [])

  useEffect(() => {
    // Initial load
    loadUserAndVehicles()

    // Re-load on auth state change
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session?.user) {
        setCurrentUserId(null)
        setVehicles([])
        setIsAdmin(false)
      } else {
        loadUserAndVehicles()
      }
    })

    return () => sub?.subscription?.unsubscribe?.()
  }, [loadUserAndVehicles])

  // =========================
  // Handlers
  // =========================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!currentUserId) {
      alert('Please sign in again')
      return
    }

    // Coerce numeric fields to numbers
    const dailyRate = formData.daily_rate ? Number(formData.daily_rate) : null
    const purchasePrice = formData.purchase_price ? Number(formData.purchase_price) : null

    const payload = {
      plate_number: formData.plate_number || null,
      vehicle_type: formData.vehicle_type || null,
      daily_rate: dailyRate,
      purchase_price: purchasePrice,
      contract_start: formData.contract_start || null,
      contract_end: formData.contract_end || null,
      driver_name: formData.driver_name || null,
      license_number: formData.license_number || null,
      added_by: currentUserId, // still store owner in DB
    }

    try {
      await apiCreateVehicle(payload)
      alert('Vehicle added!')
      setFormData({
        plate_number: '',
        vehicle_type: '',
        daily_rate: '',
        purchase_price: '',
        contract_start: '',
        contract_end: '',
        driver_name: '',
        license_number: '',
      })
      setShowForm(false)
      await loadUserAndVehicles()
    } catch (err) {
      console.error('[Vehicles] insert error:', err)
      alert(err.message || 'Failed to add vehicle.')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return

    try {
      await apiDeleteVehicle(id)
      await loadUserAndVehicles()
    } catch (err) {
      console.error('[Vehicles] delete error:', err)
      alert(err.message || 'Failed to delete.')
    }
  }

  const handleEditOpen = (row) => {
    setEditData(row)
    setEditDialogOpen(true)
  }

  const handleEditSave = async () => {
    if (!editData?.id) return

    const updateData = {
      ...editData,
      daily_rate:
        editData.daily_rate !== '' && editData.daily_rate !== null
          ? Number(editData.daily_rate)
          : null,
      purchase_price:
        editData.purchase_price !== '' && editData.purchase_price !== null
          ? Number(editData.purchase_price)
          : null,
    }

    const { id, ...rest } = updateData

    try {
      await apiUpdateVehicle(id, rest)
      setEditDialogOpen(false)
      await loadUserAndVehicles()
    } catch (err) {
      console.error('[Vehicles] update error:', err)
      alert(err.message || 'Failed to save.')
    }
  }

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(vehicles)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Vehicles')
    XLSX.writeFile(workbook, 'vehicle_list.xlsx')
  }

  const exportToPDF = () => {
    const doc = new jsPDF()
    autoTable(doc, {
      head: [['Plate', 'Type', 'Rate', 'Purchase', 'Start', 'End', 'Driver', 'License']],
      body: vehicles.map((v) => [
        v.plate_number,
        v.vehicle_type,
        v.daily_rate,
        v.purchase_price,
        v.contract_start,
        v.contract_end,
        v.driver_name,
        v.license_number,
      ]),
    })
    doc.save('vehicle_list.pdf')
  }

  // =========================
  // Filters
  // =========================
  const filtered = vehicles.filter((v) => {
    const term = searchTerm.toLowerCase()
    const matchesSearch =
      v.plate_number?.toLowerCase().includes(term) ||
      v.driver_name?.toLowerCase().includes(term) ||
      v.license_number?.toLowerCase().includes(term)

    const matchesDriver = filterDriver ? v.driver_name === filterDriver : true

    const matchesDate = filterDate
      ? (v.contract_start || '').slice(0, 10) === filterDate
      : true

    return matchesSearch && matchesDriver && matchesDate
  })

  const drivers = [...new Set(filtered.map((v) => v.driver_name).filter(Boolean))]

  // =========================
  // Render
  // =========================
  return (
    <Box p={2}>
      <Typography variant="h5" mb={2}>
        Vehicle List
        <Button
          startIcon={<AddIcon />}
          onClick={() => setShowForm(!showForm)}
          sx={{ ml: 2 }}
        >
          {showForm ? 'Hide Form' : 'Add More'}
        </Button>
      </Typography>

      <Collapse in={showForm}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {[
                ['plate_number', 'Plate Number'],
                ['vehicle_type', 'Vehicle Type'],
                ['daily_rate', 'Daily Rate'],
                ['purchase_price', 'Purchase Price'],
                ['driver_name', 'Driver Name'],
                ['license_number', 'License Number'],
              ].map(([name, label]) => (
                <Grid item xs={12} sm={6} md={3} key={name}>
                  <TextField
                    name={name}
                    label={label}
                    value={formData[name]}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="contract_start"
                  label="Contract Start"
                  type="date"
                  value={formData.contract_start}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="contract_end"
                  label="Contract End"
                  type="date"
                  value={formData.contract_end}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Collapse>

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
            placeholder="Plate / Driver / License"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Filter by Driver"
            value={filterDriver}
            onChange={(e) => setFilterDriver(e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {drivers.map((d, i) => (
              <MenuItem key={i} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            type="date"
            label="Filter by Start Date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Box mb={2}>
        <Button onClick={exportToExcel} startIcon={<DownloadIcon />} sx={{ mr: 2 }}>
          Export Excel
        </Button>
        <Button onClick={exportToPDF} startIcon={<DownloadIcon />}>
          Export PDF
        </Button>
      </Box>

      <Paper>
        <DataGrid
          rows={filtered.map((v) => ({ id: v.id, ...v }))}
          columns={[
            { field: 'plate_number', headerName: 'Plate Number', flex: 1 },
            { field: 'vehicle_type', headerName: 'Type', flex: 1 },
            { field: 'daily_rate', headerName: 'Daily Rate', flex: 1 },
            { field: 'purchase_price', headerName: 'Purchase Price', flex: 1 },
            { field: 'contract_start', headerName: 'Start Date', flex: 1 },
            { field: 'contract_end', headerName: 'End Date', flex: 1 },
            { field: 'driver_name', headerName: 'Driver', flex: 1 },
            { field: 'license_number', headerName: 'License', flex: 1 },
            {
              field: 'edit',
              headerName: '',
              sortable: false,
              filterable: false,
              renderCell: (params) => (
                <IconButton onClick={() => handleEditOpen(params.row)} color="primary">
                  <EditIcon />
                </IconButton>
              ),
            },
            {
              field: 'delete',
              headerName: '',
              sortable: false,
              filterable: false,
              renderCell: (params) => (
                <IconButton onClick={() => handleDelete(params.row.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              ),
            },
          ]}
          autoHeight
          pageSizeOptions={[8, 16, 32]}
          paginationModel={{ pageSize: 8, page }}
          onPaginationModelChange={(model) => setPage(model.page)}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Vehicle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {editData &&
              Object.keys(formData).map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    label={key.replace(/_/g, ' ').toUpperCase()}
                    name={key}
                    type={key.includes('date') ? 'date' : 'text'}
                    value={editData[key] ?? ''}
                    onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={key.includes('date') ? { shrink: true } : {}}
                  />
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}